// The one request the reject panel issues, lifted out of it.
//
// The panel itself is a read-only view of a snapshot the page hands it; this is
// the exception — clicking a trader in the tape fetches that trader's live cap
// decomposition on demand. Keeping the fetch here lets the panel stay pure and
// mountable from a test with no network at all, while the request, its trigger
// and its timing are unchanged: still one GET per click, still fired from the
// click itself, still nothing on mount.

import { useCallback, useState } from 'react';
import { apiFetch } from '@/stores/authStore';
import type { CapDebug } from './types';

export interface CapInspectorState {
  /** The trader being inspected, or null when the panel is closed. Set the
   *  instant a row is clicked — before the response lands — because the panel
   *  opens immediately and shows its own loading line. */
  userId: string | null;
  cap: CapDebug | null;
  err: string | null;
  loading: boolean;
  inspect: (userId: string) => void;
  close: () => void;
}

export function useCapInspector(): CapInspectorState {
  const [userId, setUserId] = useState<string | null>(null);
  const [cap, setCap] = useState<CapDebug | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const inspect = useCallback(async (id: string) => {
    setUserId(id);
    setCap(null);
    setErr(null);
    setLoading(true);
    try {
      const res = await apiFetch(`/api/admin/trader-cap-debug?userId=${encodeURIComponent(id)}`);
      if (!res.ok) {
        setErr(`HTTP ${res.status}`);
        return;
      }
      setCap((await res.json()) as CapDebug);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'request failed');
    } finally {
      // Runs on the early return too, so a non-2xx clears `loading` and the
      // panel shows the error rather than spinning forever.
      setLoading(false);
    }
  }, []);

  const close = useCallback(() => setUserId(null), []);

  return { userId, cap, err, loading, inspect, close };
}
